import React, { createContext, useState, useCallback, useRef, ReactNode } from 'react';

import ToastContainer from '../../shared/components/ToastContainer';

interface Toast {
  id: number | string;
  title: string;
  message: string;
  duration: number;
  type: string;
}

interface ToastContextType {
  showSuccess: (title: string, message?: string) => number | string | null;
  showError: (title: string, message?: string) => number | string | null;
  addToast: (title: string, message?: string, duration?: number, type?: string) => number | string | null;
  removeToast: (id: number | string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// useToast is exported from ./useToast to satisfy react-refresh/only-export-components

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const recentToastsRef = useRef<Map<string, number>>(new Map()); // Track recent toasts to prevent duplicates

  const addToast = useCallback((title: string, message: string = '', duration: number = 8000, type: string = 'success') => {
    // Create a unique key for this toast based on title, message, and type
    const toastKey = `${type}-${title}-${message}`;
    const now = Date.now();

    // Check if we've shown this exact toast recently (within 1 second)
    const lastShown = recentToastsRef.current.get(toastKey);
    if (lastShown && now - lastShown < 1000) {
      
      return null;
    }

    // Record this toast
    recentToastsRef.current.set(toastKey, now);

    // Clean up old entries (older than 5 seconds)
    recentToastsRef.current.forEach((timestamp, key) => {
      if (now - timestamp > 5000) {
        recentToastsRef.current.delete(key);
      }
    });

    const id = Date.now() + Math.random();
    const newToast: Toast = { id, title, message, duration, type };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    return id;
  }, []);

  const removeToast = useCallback((id: number | string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback((title: string, message: string = '') => {
    return addToast(title, message, 8000, 'success');
  }, [addToast]);

  const showError = useCallback((title: string, message: string = '') => {
    return addToast(title, message, 8000, 'error');
  }, [addToast]);

  const value: ToastContextType = {
    showSuccess,
    showError,
    addToast,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
